import { storage, StorageKey } from 'utils/typedStorage';
import { useEffect, useCallback } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { getCalendar } from 'api/generated/api/calendar';
import { getValidOrgId, getValidUserId } from 'utils/storageUtils';
import { useCalendarIntegrationStore } from 'stores/calendarIntegrationStore';

// Shared across hook consumers because they all write the same store.
let latestCalendarRequest = 0;

/**
 * Hook to manage calendar integration state globally.
 * Fetches calendar integration status only once and caches the result.
 * Subsequent calls will use the cached value unless force refresh is requested.
 */
export function useCalendarIntegration() {
  const calendarState = useCalendarIntegrationStore(
    useShallow((s) => ({
      calendarConnected: s.calendarConnected,
      userCalendarConnected: s.userCalendarConnected,
      provider: s.provider,
      meetingProvidersAvailable: s.meetingProvidersAvailable,
      calendarIntegrationType: s.calendarIntegrationType,
      isLoading: s.isLoading,
      isLoaded: s.isLoaded,
      error: s.error,
    }))
  );

  const fetchCalendarIntegration = useCallback(async (forceRefresh = false) => {
    const {
      isLoading,
      isLoaded,
      generation,
      setLoading,
      applyIntegration,
      setError,
    } = useCalendarIntegrationStore.getState();
    // Don't fetch if already loaded/loading unless force refresh
    if (!forceRefresh && (isLoading || isLoaded)) {
      return;
    }

    const orgId = getValidOrgId();
    const userId = getValidUserId();
    const authToken = storage.get(StorageKey.AuthToken);

    // Don't fetch if missing required auth data
    if (!orgId || !userId || !authToken) {
      return;
    }

    setLoading(true);
    const request = ++latestCalendarRequest;
    const isStale = () =>
      request !== latestCalendarRequest ||
      useCalendarIntegrationStore.getState().generation !== generation;

    try {
      const data = await getCalendar().calendarGetCalendarIntegration(
        orgId,
        userId
      );
      // The store was reset (logout) while this request was in flight.
      if (isStale()) {
        return;
      }
      applyIntegration(data);
    } catch (error) {
      if (isStale()) {
        return;
      }
      console.error('Error fetching calendar integration:', error);
      setError();
    }
  }, []);

  return {
    calendarState,
    fetchCalendarIntegration,
    refetch: () => fetchCalendarIntegration(true),
  };
}

/**
 * Hook that automatically fetches calendar integration on mount if not already loaded.
 * Use this in top-level components like SideNavBar.
 */
export function useCalendarIntegrationAutoFetch() {
  const { calendarState, fetchCalendarIntegration } = useCalendarIntegration();

  useEffect(() => {
    void fetchCalendarIntegration();
  }, [fetchCalendarIntegration]);

  return calendarState;
}

/**
 * Hook that ensures calendar integration is fetched, then exposes the reactive state.
 * Use this in components that need the calendar state but shouldn't own the fetch trigger.
 */
export function useCalendarIntegrationWait() {
  const { calendarState, fetchCalendarIntegration } = useCalendarIntegration();

  // Trigger fetch if not already loaded/loading
  useEffect(() => {
    void fetchCalendarIntegration();
  }, [fetchCalendarIntegration]);

  return {
    calendarState,
  };
}
